import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";

type TitleAndOpen = {
  open: boolean;
  title: string;
  onClose: () => void; // ✅ Always required now
};

// --- Content union
type WithDescription = {
  description: string;
  children?: never;
};

type WithChildren = {
  children: React.ReactNode;
  description?: never;
};

// --- Button-based footer
type WithStringButtons = {
  actions?: never;
  okayButtonText?: string;
  cancelButtonText?: string;
  onConfirm: () => void;
};

type WithCustomButtons = {
  actions?: never;
  okayButtonText?: React.ReactNode;
  cancelButtonText?: React.ReactNode;
  onConfirm?: never;
};

// --- Custom footer override
type WithCustomActions = {
  actions: React.ReactNode;
  okayButtonText?: never;
  cancelButtonText?: never;
  onConfirm?: never;
};

type ConfirmationDialogProps = TitleAndOpen &
  (WithDescription | WithChildren) &
  (WithStringButtons | WithCustomButtons | WithCustomActions);

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  title,
  description,
  children,
  okayButtonText = "OK",
  cancelButtonText = "Cancel",
  onClose,
  onConfirm,
  actions,
}) => {
  const isOkayString = typeof okayButtonText === "string";
  const isCancelString = typeof cancelButtonText === "string";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="confirmation-dialog-title"
    >
      <DialogTitle
        id="confirmation-dialog-title"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: 18,
        }}
      >
        <Box>{title}</Box>
        {onClose && (
          <IconButton
            aria-label="close"
            data-testid="confirmation-dialog-close-btn"
            onClick={onClose}
            size="small"
            sx={{ ml: 2 }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent>
        {children ? (
          children
        ) : (
          <DialogContentText>{description}</DialogContentText>
        )}
      </DialogContent>

      <DialogActions>
        {actions ? (
          actions
        ) : (
          <>
            {isCancelString ? (
              <Button
                variant="outlined"
                color="secondary"
                onClick={onClose}
                data-testid="confirmation-dialog-cancel-btn"
              >
                {cancelButtonText}
              </Button>
            ) : (
              cancelButtonText
            )}

            {isOkayString ? (
              <Button
                variant="contained"
                color="primary"
                onClick={onConfirm}
                data-testid="confirmation-dialog-confirm-btn"
              >
                {okayButtonText}
              </Button>
            ) : (
              okayButtonText
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
